// DESCRIPTION: Payment settings tab component for configuring Stripe, currencies,
// and payment gateway options in the Nemtly Booking admin settings.

import { __ } from '@wordpress/i18n';
import {
    TextControl,
    SelectControl,
    ToggleControl,
    TextareaControl,
    Button,
    Notice,
    Card,
    CardBody,
    CardHeader,
    __experimentalDivider as Divider,
} from '@wordpress/components';

/**
 * Payment Settings Component
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {Object}   props.settings      Current settings object.
 * @param {Function} props.updateSetting  Callback to update a single setting key.
 * @param {Function} props.addNotice      Callback to show a toast notice.
 *
 * @return {JSX.Element}
 */
function PaymentSettings({ settings, updateSetting, addNotice }) {
    const currencyOptions = [
        { label: 'USD - US Dollar', value: 'USD' },
        { label: 'EUR - Euro', value: 'EUR' },
        { label: 'GBP - British Pound', value: 'GBP' },
        { label: 'CAD - Canadian Dollar', value: 'CAD' },
        { label: 'AUD - Australian Dollar', value: 'AUD' },
        { label: 'JPY - Japanese Yen', value: 'JPY' },
    ];

    const paymentGatewayOptions = [
        { label: 'Stripe', value: 'stripe' },
    ];

    return (
        <div className="nb-space-y-6">
            {/* Stripe Settings Card */}
            <Card className="nb-border nb-border-slate-200 nb-shadow-sm">
                <CardHeader className="nb-bg-slate-50">
                    <div className="nb-flex nb-items-center nb-gap-3">
                        <div className="nb-w-8 nb-h-8 nb-bg-slate-600 nb-rounded-lg nb-flex nb-items-center nb-justify-center">
                            <svg className="nb-w-4 nb-h-4 nb-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="nb-text-lg nb-font-semibold nb-text-slate-900">{__('Payment Settings', 'nemtly-booking')}</h3>
                            <p className="nb-text-sm nb-text-slate-600">{__('Configure settings for accepting payments', 'nemtly-booking')}</p>
                        </div>
                    </div>
                </CardHeader>
                <CardBody className="nb-space-y-6">

                    <div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-2 nb-gap-6">
                        <SelectControl
                            label={__('Currency', 'nemtly-booking')}
                            value={settings.currency || 'USD'}
                            options={currencyOptions}
                            onChange={(value) => updateSetting('currency', value)}
                            help={__('Default currency for payments', 'nemtly-booking')}
                        />
                    </div>

                    <div className="nb-space-y-4">
                        <h4 className="nb-text-base nb-font-medium nb-text-slate-900">{__('Payment methods', 'nemtly-booking')}</h4>
                        <p className="nb-text-sm nb-text-slate-500">
                            {__('Choose which payment options to offer on the booking form. Manual payment (e.g. bank transfer, cash) creates a pending booking until you mark it as paid in Bookings.', 'nemtly-booking')}
                        </p>
                        <ToggleControl
                            label={__('Accept Stripe (card payments)', 'nemtly-booking')}
                            checked={settings.accept_stripe !== false}
                            onChange={(value) => updateSetting('accept_stripe', value)}
                            help={__('When disabled, only manual payment is used if enabled below.', 'nemtly-booking')}
                        />
                        <ToggleControl
                            label={__('Accept manual/offline payment', 'nemtly-booking')}
                            checked={settings.accept_manual_payment || false}
                            onChange={(value) => updateSetting('accept_manual_payment', value)}
                            help={__('Allow customers to choose bank transfer, cash, or invoice. You mark these as paid in Bookings.', 'nemtly-booking')}
                        />
                        {(settings.accept_manual_payment || false) && (
                            <div className="nb-space-y-3 nb-pl-4 nb-border-l-2 nb-border-slate-200">
                                <TextControl
                                    label={__('Manual payment option label', 'nemtly-booking')}
                                    value={settings.manual_payment_label || ''}
                                    onChange={(value) => updateSetting('manual_payment_label', value)}
                                    placeholder={__('e.g. Bank transfer / Invoice', 'nemtly-booking')}
                                    help={__('Label shown on the booking form for the manual payment option.', 'nemtly-booking')}
                                />
                                <TextareaControl
                                    label={__('Manual payment instructions', 'nemtly-booking')}
                                    value={settings.manual_payment_instructions || ''}
                                    onChange={(value) => updateSetting('manual_payment_instructions', value)}
                                    placeholder={__('e.g. Pay to account X. Use booking reference as payment reference.', 'nemtly-booking')}
                                    help={__('Shown to the customer after they choose manual payment.', 'nemtly-booking')}
                                    rows={4}
                                />
                            </div>
                        )}
                    </div>

                    <div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-2 nb-gap-6">
                        <SelectControl
                            label={__('Select Payment Gateway', 'nemtly-booking')}
                            value={settings.payment_gateway || 'stripe'}
                            options={paymentGatewayOptions}
                            onChange={(value) => updateSetting('payment_gateway', value)}
                            help={__('Select the payment gateway to use', 'nemtly-booking')}
                        />
                    </div>

                    <Divider />

                    <div className="nb-space-y-4">
                        <h4 className="nb-text-base nb-font-medium nb-text-slate-900">{__('Stripe API Keys', 'nemtly-booking')}</h4>
                        <p className="nb-text-sm nb-text-slate-500 nb-mb-4">
                            {__('You can find these keys in your Stripe Dashboard under Developers > API Keys.', 'nemtly-booking')}
                        </p>

                        <TextControl
                            label={__('Publishable Key', 'nemtly-booking')}
                            value={settings.stripe_publishable_key || ''}
                            onChange={(value) => updateSetting('stripe_publishable_key', value)}
                            placeholder="pk_test_..."
                            help={__('Starts with pk_', 'nemtly-booking')}
                        />

                        <TextControl
                            label={__('Secret Key', 'nemtly-booking')}
                            value={settings.stripe_secret_key || ''}
                            onChange={(value) => updateSetting('stripe_secret_key', value)}
                            type="password"
                            placeholder="sk_test_..."
                            help={__('Starts with sk_ or rk_', 'nemtly-booking')}
                        />
                    </div>

                    <Divider />

                    <div className="nb-space-y-4">
                        <h4 className="nb-text-base nb-font-medium nb-text-slate-900">{__('Webhook Configuration', 'nemtly-booking')}</h4>
                        <p className="nb-text-sm nb-text-slate-500 nb-mb-4">
                            {__('Webhooks are required to update booking status after payment. Add the endpoint URL in Stripe Dashboard (Developers → Webhooks → Add endpoint), then paste the signing secret below.', 'nemtly-booking')}
                        </p>

                        <TextControl
                            label={__('Webhook endpoint URL', 'nemtly-booking')}
                            value={settings.stripe_webhook_url || ''}
                            onChange={(value) => updateSetting('stripe_webhook_url', value)}
                            help={__('Copy this URL and add it as an endpoint in Stripe. Subscribe to payment_intent.succeeded and payment_intent.payment_failed.', 'nemtly-booking')}
                        />
                        <div className="nb-flex nb-items-center nb-gap-2 nb-mt-2">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    const url = (settings.stripe_webhook_url || '').trim();
                                    if (url && navigator.clipboard?.writeText) {
                                        navigator.clipboard.writeText(url);
                                        if (addNotice) {
                                            addNotice(__('URL copied to clipboard.', 'nemtly-booking'), 'success');
                                        }
                                    }
                                }}
                            >
                                {__('Copy URL', 'nemtly-booking')}
                            </Button>
                            {settings.stripe_webhook_url_default && settings.stripe_webhook_url !== settings.stripe_webhook_url_default && (
                                <Button
                                    variant="tertiary"
                                    onClick={() => updateSetting('stripe_webhook_url', settings.stripe_webhook_url_default)}
                                >
                                    {__('Reset to default', 'nemtly-booking')}
                                </Button>
                            )}
                        </div>
                        {settings.stripe_webhook_url && !settings.stripe_webhook_url.startsWith('https://') && (
                            <Notice status="warning" isDismissible={false} className="nb-mt-2">
                                {__('Stripe requires HTTPS for webhook endpoints. This URL uses HTTP. Use an HTTPS site or Stripe CLI for local testing.', 'nemtly-booking')}
                            </Notice>
                        )}

                        <TextControl
                            label={__('Webhook signing secret', 'nemtly-booking')}
                            value={settings.stripe_webhook_secret || ''}
                            onChange={(value) => updateSetting('stripe_webhook_secret', value)}
                            type="password"
                            placeholder="whsec_..."
                            help={__('After adding the endpoint in Stripe, copy the signing secret (starts with whsec_) and paste it here.', 'nemtly-booking')}
                        />
                    </div>
                </CardBody>
            </Card>
        </div>
    );
}

export default PaymentSettings;
